import { Tool } from '../types';

/** Curated one-liners for high-traffic tools when DB copy is English boilerplate. */
const CURATED_ZH: Record<string, string> = {
  'claude-computer-use': '会操作电脑界面，先在隔离环境试，别接生产账号。',
  'google-veo': 'Google 官方视频生成。只从 DeepMind / Google 入口进入。',
  chatgpt: '英文通用对话。只从 OpenAI 官网进入，不是国内默认入口。',
  'chatgpt-openai': '英文通用对话。只从 OpenAI 官网进入，不是国内默认入口。',
  claude: '长文和英文说明更合适。要操作电脑界面看 Computer Use。',
  cursor: '在仓库里改代码再用。先搭页面原型可看 Bolt.new。',
  midjourney: '要风格和提示词社区再开。国内直连出图先看即梦。',
  heygen: '要数字人口播再用。空镜短视频看可灵或视频对照短文。',
  suno: '样曲可以出，发行前先看授权。',
  runway: '要镜头运动和剪辑感再开。国内空镜可先看可灵。',
  kling: '国内做空镜短视频可先试。只走 klingai.com。',
  jimeng: '剪映生态里的视频和图像草稿。',
  kimi: '长 PDF 不要先丢给通用聊天。',
  coze: '任务写清再搭机器人。要自托管再看 Dify。',
  dify: '要自己部署工作流再选。国内网页快搭先看扣子。',
  gemini: '人已经在 Google 账号里再用。国内短办公仍看豆包那一轮。',
  perplexity: '要带来源的检索，不是写草稿。',
  notebooklm: '先有一批资料，再让它只根据这些资料回答。',
  gamma: '大纲已经有了，再做成演示。',
  manus: '要自动跑完多步任务再试。',
  deepseek: '要看推理过程再加一轮。日常办公仍用豆包那一轮。',
  minimax: '国内配音和视频草稿。会议纪要仍看讯飞星火。',
  'github-copilot': '仓库在 GitHub 上再开。整仓大改可对照 Cursor。',
  'microsoft-copilot': '人已经在 Office / Microsoft 365 里再用。',
  'adobe-firefly': '已经在用 Adobe 再出图。',
  leonardo: '出图先固定同一句提示词再比较。',
  'bolt-new': '先搭页面原型，再进仓库用 Cursor。',
  yuanbao: '人已经在微信生态里再用。',
  'luma-dream-machine': '先出镜头草稿再谈成片。国内空镜可先看可灵。',
  'iflytek-spark': '中文会议与语音输入更合适。',
  elevenlabs: '发出配音前先看授权。中文会议仍看讯飞星火。',
  '扣子-coze': '国内搭机器人、接知识库的网页入口。任务写清再开始。',
  chatglm: '智谱 GLM 的中文对话入口。要试另一家国产模型时再加一轮。',
  mistral: '开源权重与 API 对话。长英文成稿仍可对照 Claude。',
  'mistral-ai': '开源权重与 API 对话。长英文成稿仍可对照 Claude。',
  aippt: '大纲做成演示文稿。要更快出成片可对照 Gamma。',
};

const CURATED_EN: Record<string, string> = {
  'claude-computer-use': 'It operates a computer UI. Try it away from production accounts.',
  'google-veo': 'Google’s official video model. Open it only from Google / DeepMind.',
  chatgpt: 'General English chat. Open only from OpenAI — not a mainland default.',
  'chatgpt-openai': 'General English chat. Open only from OpenAI — not a mainland default.',
  claude: 'Better for long English writing. For UI control, see Computer Use.',
  cursor: 'Edit inside a repository. Use Bolt.new first for a page prototype.',
  midjourney: 'Open when style and prompt community matter. Use Jimeng for mainland access.',
  heygen: 'For a talking presenter. For footage, compare Kling or the video note.',
  suno: 'Fine for song drafts. Read the license before you release.',
  runway: 'Open when camera move and editability matter.',
  kling: 'Mainland text-to-video footage. Open only klingai.com.',
  jimeng: 'Image and video drafts near CapCut / Jianying.',
  kimi: 'Use when the file is a long PDF.',
  coze: 'Build a bot only after the task is clear.',
  dify: 'Self-host the workflow. Use Coze for a faster China-site path.',
  gemini: 'Belongs with a Google account, not every chat app.',
  perplexity: 'Sourced search, not drafting.',
  notebooklm: 'Answers from a pack of sources you upload.',
  gamma: 'Turns an outline into slides.',
  manus: 'Open only when the multi-step job is clear.',
  deepseek: 'Open when you need the reasoning steps.',
  minimax: 'Mainland voice and video draft. Meeting notes stay with iFlytek Spark.',
  'github-copilot': 'Low-friction add-on when the repo is already on GitHub.',
  'microsoft-copilot': 'Belongs inside Microsoft 365 / Office.',
  'adobe-firefly': 'Fits teams already inside Adobe.',
  leonardo: 'Lock one prompt before you compare still images.',
  'bolt-new': 'Draft a front-end prototype, then edit the repo in Cursor.',
  yuanbao: 'Belongs next to WeChat work.',
  'luma-dream-machine': 'For a camera draft before a finished cut.',
  'iflytek-spark': 'Chinese meetings and voice input.',
  mistral: 'Open-weight models and a chat API. Compare with Claude for long English writing.',
  aippt: 'Turns an outline into slides. Compare Gamma when the deck must look finished fast.',
};

/** English display names for mainland brands when name_en is empty. */
const NAME_EN_BY_KEY: Record<string, string> = {
  通义千问: 'Qwen',
  文心一言: 'ERNIE Bot',
  豆包: 'Doubao',
  智谱清言: 'Zhipu Qingyan',
  讯飞星火: 'iFlytek Spark',
  腾讯元宝: 'Yuanbao',
  扣子: 'Coze',
  '扣子 Coze': 'Coze',
  '扣子-coze': 'Coze',
  即梦: 'Jimeng',
  '即梦 AI': 'Jimeng',
  可灵: 'Kling',
  '可灵 AI': 'Kling',
  秘塔AI搜索: 'Metaso',
  阶跃星辰: 'StepFun',
  百川智能: 'Baichuan',
  办公小浣熊: 'Raccoon',
  美图设计室: 'Meitu Design',
  chatglm: 'ChatGLM',
  yuanbao: 'Yuanbao',
  kimi: 'Kimi',
  jimeng: 'Jimeng',
  kling: 'Kling',
  coze: 'Coze',
  'iflytek-spark': 'iFlytek Spark',
};

export const getToolNameByLanguage = (tool: Tool, language: 'zh-CN' | 'en-US'): string => {
  if (language === 'zh-CN') {
    return tool.name_zh || tool.name || tool.name_en || '';
  }
  const slug = String(tool.slug || '').trim();
  const name = String(tool.name || '').trim();
  if (tool.name_en && !looksChinese(tool.name_en)) return tool.name_en;
  if (NAME_EN_BY_KEY[slug]) return NAME_EN_BY_KEY[slug];
  if (NAME_EN_BY_KEY[name]) return NAME_EN_BY_KEY[name];
  if (name && !looksChinese(name)) return name;
  return tool.name_en || tool.name_zh || name;
};

function looksChinese(text?: string | null): boolean {
  return !!text && /[\u4e00-\u9fff]/.test(text);
}

function isBoilerplate(text?: string | null): boolean {
  if (!text) return true;
  return /widely used AI platform|popular AI platform|based on advanced AI|is a widely used|— popular AI platform|from curated AI list|— from curated|curated AI list/i.test(
    text,
  );
}

function curatedFor(tool: Tool, language: 'zh-CN' | 'en-US'): string | null {
  const slug = String(tool.slug || '').toLowerCase();
  const name = String(tool.name || '').trim();
  const map = language === 'zh-CN' ? CURATED_ZH : CURATED_EN;
  // Exact slug only — never substring-match (AnonChatGPT must not inherit ChatGPT copy).
  if (map[slug]) return map[slug];

  // Exact Chinese product names only (whole name, not substring of a clone listing).
  if (language === 'zh-CN') {
    const exactZh: Record<string, string> = {
      豆包: '国内可直接打开的中文助手。适合日常对话、写作和办公草稿。',
      通义千问: '阿里云生态里的中文助手。团队已在阿里云时优先。',
      文心一言: '靠近百度搜索的中文助手。适合要留在百度体系里的工作。',
      智谱清言: '智谱 GLM 的中文对话入口。要试另一家国产模型时再加一轮。',
      讯飞星火: '中文会议与语音输入更合适。配音成片仍看 ElevenLabs。',
      即梦: '剪映生态里的视频和图像草稿。',
      '即梦 AI': '剪映生态里的视频和图像草稿。',
      可灵: '国内做空镜短视频可先试。只走 klingai.com。',
      '可灵 AI': '国内做空镜短视频可先试。只走 klingai.com。',
      腾讯元宝: '人已经在微信生态里再用。',
      秘塔AI搜索: '国内可直连的检索型助手，答案尽量带来源。',
      阶跃星辰: '国内可试的大模型入口之一。短办公仍可先用豆包那一轮对照。',
      百川智能: '国内可试的大模型入口之一。短办公仍可先用豆包那一轮对照。',
      办公小浣熊: '偏办公文档场景的国内助手。',
      美图设计室: '美图生态里的设计与出图入口。',
      千问创作: '通义创作向入口。团队已在阿里云时优先。',
    };
    if (exactZh[name]) return exactZh[name];
  }
  return null;
}

export const getToolDescriptionByLanguage = (tool: Tool, language: 'zh-CN' | 'en-US'): string => {
  if (language === 'zh-CN') {
    const zh = tool.description_zh || tool.short_description || tool.description;
    if (looksChinese(zh) && !isBoilerplate(zh)) return String(zh);
    return curatedFor(tool, 'zh-CN') || getToolShortDescriptionByLanguage(tool, 'zh-CN');
  }
  const en = tool.description_en || tool.description || tool.short_description;
  if (en && !isBoilerplate(en)) return String(en);
  return curatedFor(tool, 'en-US') || getToolShortDescriptionByLanguage(tool, 'en-US');
};

export const getToolShortDescriptionByLanguage = (tool: Tool, language: 'zh-CN' | 'en-US'): string => {
  const curated = curatedFor(tool, language);
  if (language === 'zh-CN') {
    const zh = tool.description_zh || tool.short_description || tool.description;
    if (looksChinese(zh) && !isBoilerplate(zh)) return String(zh).slice(0, 96);
    if (curated) return curated;
    const name = tool.name_zh || tool.name || '该工具';
    return `${name}：先看官网能力，再对照本站选型短文。`;
  }
  const en = tool.description_en || tool.short_description || tool.description || '';
  if (en && !isBoilerplate(en)) return String(en).slice(0, 140);
  if (curated) return curated;
  return `${tool.name_en || tool.name}: open the official site, then compare peers in Buying notes.`;
};
